import React, { useRef, useState } from "react";
import { PAGE_SIZE_PRESETS } from "../../constants/pageDimensions";
import { CELL_LAYOUT_TEMPLATES } from "../../constants/cellLayoutTemplates";
import { COMIC_STYLES } from "../../constants/comicStyles";
import { BOARD_LAYOUTS, FRAME_ASPECTS, MATTES } from "../../constants/storyboard";
import {
  useLayoutTemplateKey, useGutterSize, usePageSizeKey,
  useExportDpi, useExportFormat, useShowGuides,
  useShowReadingOrder, useReadingDirection, useDarkMode,
  useCanUndo, useCanRedo,
  useSnapEnabled, useShowSnapGrid, useComicStyleKey, useKnifeMode, useStoryboard,
  useEditorStore, useEditorStoreApi,
} from "../../store/editorStoreSelectors";
import { makeToolbarStyles } from "./toolbarStyles";

const sectionStyle = { display: "flex", alignItems: "center", gap: 8 };

const ToolbarRow1 = ({
  onChangeLayoutTemplate,
  onDownload,
  onClearSavedData,
  onExportJSON,
  onImportJSON,
  onCustomGrid,
  onGutterSizeChange,
  onPageSizeChange,
  onStoryboardChange,
  effectivePanelStyle,
  openPanel,
  togglePanel,
  rowStyle,
}) => {
  const selectedLayoutTemplateKey = useLayoutTemplateKey();
  const gutterSize = useGutterSize();
  const storeApi = useEditorStoreApi();

  // A whole slider scrub is one undo step: open a gesture on press and close
  // it on release, wherever the pointer ends up.
  const beginGutterScrub = () => {
    storeApi.getState().beginGesture();
    window.addEventListener("pointerup", () => storeApi.getState().endGesture(), { once: true });
  };
  const pageSizeKey = usePageSizeKey();
  const exportDpi = useExportDpi();
  const exportFormat = useExportFormat();
  const showGuides = useShowGuides();
  const showReadingOrder = useShowReadingOrder();
  const readingDirection = useReadingDirection();
  const darkMode = useDarkMode();
  const canUndo = useCanUndo();
  const canRedo = useCanRedo();
  const snapEnabled = useSnapEnabled();
  const showSnapGrid = useShowSnapGrid();
  const comicStyleKey = useComicStyleKey();
  const storyboard = useStoryboard();
  const onComicStyleChange = useEditorStore((s) => s.setComicStyleKey);

  const onUndo = useEditorStore((s) => s.undo);
  const onRedo = useEditorStore((s) => s.redo);
  const onExportFormatChange = useEditorStore((s) => s.setExportFormat);
  const onExportDpiChange = useEditorStore((s) => s.setExportDpi);
  const onToggleGuides = useEditorStore((s) => s.toggleGuides);
  const knifeMode = useKnifeMode();
  const setKnifeMode = useEditorStore((s) => s.setKnifeMode);
  const onToggleReadingOrder = useEditorStore((s) => s.toggleReadingOrder);
  const onReadingDirectionChange = useEditorStore((s) => s.setReadingDirection);
  const onToggleDarkMode = useEditorStore((s) => s.toggleDarkMode);
  const onToggleSnapEnabled = useEditorStore((s) => s.toggleSnapEnabled);
  const onToggleShowSnapGrid = useEditorStore((s) => s.toggleShowSnapGrid);

  const jsonInputRef = useRef(null);
  const [customRows, setCustomRows] = useState(3);
  const [customCols, setCustomCols] = useState(3);
  const layoutTemplates = CELL_LAYOUT_TEMPLATES;

  const {
    labelStyle,
    selectStyle,
    buttonStyle,
    iconButtonStyle,
    activeIconButtonStyle,
    sliderStyle,
    dropdownTriggerStyle,
  } = makeToolbarStyles(darkMode);

  const handleJSONImport = (event) => {
    const file = event.target.files && event.target.files[0];
    if (file && onImportJSON) onImportJSON(file);
    if (event.target) event.target.value = "";
  };

  const triggerJSONImport = () => {
    if (jsonInputRef.current) jsonInputRef.current.click();
  };

  return (
    // Wraps rather than clipping, so no control is ever out of reach
    <div style={{ ...rowStyle, flexWrap: "wrap", rowGap: 6 }}>
      {Array.isArray(layoutTemplates) && layoutTemplates.length > 0 && (
        <div style={{ ...sectionStyle, gap: 10, flexWrap: "wrap", rowGap: 6 }}>
          {storyboard ? (
            // Board pages: frames are generated from these, never hand-cut
            <div style={{ ...sectionStyle, flexWrap: "wrap", rowGap: 6 }}>
              <span style={labelStyle}>Board:</span>
              <select
                aria-label="Board layout"
                title="How many frames per page, and where the notes go"
                style={{ ...selectStyle, minWidth: 150 }}
                value={storyboard.layoutKey}
                onChange={(e) => onStoryboardChange?.({ layoutKey: e.target.value })}
              >
                {BOARD_LAYOUTS.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
              </select>
              <span style={labelStyle}>Frame:</span>
              <select
                aria-label="Frame aspect ratio"
                title="The aspect ratio every frame is locked to"
                style={{ ...selectStyle, minWidth: 150 }}
                value={storyboard.aspectKey}
                onChange={(e) => onStoryboardChange?.({ aspectKey: e.target.value })}
              >
                {FRAME_ASPECTS.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
              </select>
              <span style={labelStyle}>Matte:</span>
              <select
                aria-label="Matte"
                title="Letterbox bars or a crop guide for another delivery ratio"
                style={{ ...selectStyle, minWidth: 140 }}
                value={storyboard.matteKey}
                onChange={(e) => onStoryboardChange?.({ matteKey: e.target.value })}
              >
                {MATTES.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
              <button
                type="button"
                aria-label="Safe areas"
                aria-pressed={Boolean(storyboard.safeAreas)}
                title="Show action-safe (93%) and title-safe (90%) areas in every frame"
                style={storyboard.safeAreas ? activeIconButtonStyle : iconButtonStyle}
                onClick={() => onStoryboardChange?.({ safeAreas: !storyboard.safeAreas })}
              >
                <span className="material-icons" aria-hidden="true">crop_free</span>
              </button>
            </div>
          ) : (
            <div style={{ ...sectionStyle, position: "relative" }}>
              <span style={labelStyle}>Layout:</span>
              <select
                aria-label="Default layout"
                style={{ ...selectStyle, minWidth: 140 }}
                value={selectedLayoutTemplateKey}
                onChange={(event) => {
                  if (event.target.value === "__custom__") {
                    togglePanel("customGrid");
                  } else {
                    onChangeLayoutTemplate?.(event.target.value);
                  }
                }}
              >
                {layoutTemplates.map((template) => (
                  <option key={template.key} value={template.key}>{template.label}</option>
                ))}
                <option value="__custom__">Custom…</option>
              </select>
              {openPanel === "customGrid" && (
                <div style={effectivePanelStyle}>
                  <label style={{ fontSize: 12 }}>
                    Rows:
                    <input type="number" min={1} max={10} value={customRows}
                      onChange={(e) => setCustomRows(Math.max(1, Math.min(10, Number(e.target.value))))}
                      style={{ width: 40, marginLeft: 4, ...selectStyle }} />
                  </label>
                  <span style={{ fontSize: 12 }}>×</span>
                  <label style={{ fontSize: 12 }}>
                    Cols:
                    <input type="number" min={1} max={10} value={customCols}
                      onChange={(e) => setCustomCols(Math.max(1, Math.min(10, Number(e.target.value))))}
                      style={{ width: 40, marginLeft: 4, ...selectStyle }} />
                  </label>
                  <button type="button" style={buttonStyle} onClick={() => {
                    onCustomGrid?.(customRows, customCols);
                    togglePanel(null);
                  }}>Apply</button>
                  <button type="button" style={buttonStyle} onClick={() => togglePanel(null)}>Cancel</button>
                </div>
              )}
              <button
                type="button"
                aria-label="Knife: cut panels"
                aria-pressed={knifeMode}
                title="Knife — drag across a panel to cut it (or hold ⌥ and drag). Shift cuts straight. Esc to stop."
                style={knifeMode ? activeIconButtonStyle : iconButtonStyle}
                onClick={() => setKnifeMode(!knifeMode)}
              >
                <span className="material-icons" aria-hidden="true">content_cut</span>
              </button>
            {gutterSize != null && onGutterSizeChange && (
              <label title="Gutter width" style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, ...labelStyle, marginLeft: 4 }}>
                Gutter
                <input type="range" aria-label="Gutter width" min={0} max={30} step={1}
                  value={gutterSize} onChange={(e) => onGutterSizeChange(Number(e.target.value))}
                  onPointerDown={beginGutterScrub}
                  style={{ width: 60, ...sliderStyle }} />
                <span style={{ minWidth: 20, textAlign: "right" }}>{gutterSize}</span>
              </label>
            )}
            </div>
          )}

          {onPageSizeChange && (
            <div style={sectionStyle}>
              <span style={labelStyle}>Page:</span>
              <select
                aria-label="Page size"
                style={{ ...selectStyle, minWidth: 120 }}
                value={pageSizeKey || "letter"}
                onChange={(e) => onPageSizeChange(e.target.value)}
              >
                {PAGE_SIZE_PRESETS.filter((p) => p.key !== "custom").map((preset) => (
                  <option key={preset.key} value={preset.key}>{preset.label}</option>
                ))}
              </select>
            </div>
          )}

          <div style={sectionStyle}>
            <span style={labelStyle}>Style:</span>
            <select
              aria-label="Comic style"
              title="Comic book style preset"
              style={{ ...selectStyle, minWidth: 130 }}
              value={comicStyleKey || "inked"}
              onChange={(e) => onComicStyleChange(e.target.value)}
            >
              {COMIC_STYLES.map((style) => (
                <option key={style.key} value={style.key}>{style.label}</option>
              ))}
            </select>
          </div>

          {/* View: overlays and snapping, as labelled toggles */}
          <div style={{ position: "relative" }}>
            <button type="button" style={dropdownTriggerStyle} onClick={() => togglePanel("view")}
              aria-expanded={openPanel === "view"} title="Guides, reading order, and snapping">
              View <span style={{ fontSize: 10 }}>&#9662;</span>
            </button>
            {openPanel === "view" && (
              <div style={{ ...effectivePanelStyle, flexDirection: "column", alignItems: "stretch", gap: 4, padding: 8, minWidth: 210 }}>
                {[
                  ["Print guides", "straighten", showGuides, onToggleGuides],
                  ["Reading order", "format_list_numbered", showReadingOrder, onToggleReadingOrder],
                  ["Snap to grid & neighbours", "grid_3x3", snapEnabled, onToggleSnapEnabled],
                  ["Show snap grid", "grid_4x4", showSnapGrid, onToggleShowSnapGrid],
                ].map(([label, icon, on, toggle]) => (
                  <button key={label} type="button" role="menuitemcheckbox" aria-checked={Boolean(on)} onClick={toggle}
                    style={{ ...buttonStyle, display: "flex", alignItems: "center", gap: 8, textAlign: "left",
                      ...(on ? { background: "#ffe600", color: "#1a1a1a", borderColor: "#1a1a1a" } : {}) }}>
                    <span className="material-icons" aria-hidden="true" style={{ fontSize: 16 }}>{icon}</span>
                    <span style={{ flex: 1 }}>{label}</span>
                  </button>
                ))}
                {showReadingOrder && onReadingDirectionChange && (
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, paddingLeft: 4 }}>
                    Reading direction
                    <select aria-label="Reading direction" value={readingDirection || "ltr"}
                      onChange={(e) => onReadingDirectionChange(e.target.value)} style={{ ...selectStyle, width: 70 }}>
                      <option value="ltr">LTR</option>
                      <option value="rtl">RTL (manga)</option>
                    </select>
                  </label>
                )}
              </div>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button type="button" aria-label="Undo last change" title="Undo (Ctrl+Z)"
              style={iconButtonStyle} onClick={onUndo} disabled={!canUndo}>
              <span className="material-icons" aria-hidden="true">undo</span>
            </button>
            <button type="button" aria-label="Redo last change" title="Redo (Ctrl+Shift+Z)"
              style={iconButtonStyle} onClick={onRedo} disabled={!canRedo}>
              <span className="material-icons" aria-hidden="true">redo</span>
            </button>
          </div>

          {/* Export: page output and project files */}
          <div style={{ position: "relative" }}>
            <button type="button" style={dropdownTriggerStyle} onClick={() => togglePanel("export")}
              aria-expanded={openPanel === "export"} title="Export this page, or save/load the layout file">
              <span className="material-icons" aria-hidden="true" style={{ fontSize: 16 }}>download</span>
              Export <span style={{ fontSize: 10 }}>&#9662;</span>
            </button>
            {openPanel === "export" && (
              <div style={{ ...effectivePanelStyle, left: "auto", right: 0, flexDirection: "column", alignItems: "stretch", gap: 8, padding: 10, minWidth: 230 }}>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  {onExportFormatChange && (
                    <select aria-label="Export format" style={{ ...selectStyle, width: 64 }}
                      value={exportFormat || "pdf"} onChange={(e) => onExportFormatChange(e.target.value)}>
                      <option value="pdf">PDF</option>
                      <option value="png">PNG</option>
                    </select>
                  )}
                  {onExportDpiChange && (
                    <select aria-label="Export DPI" style={{ ...selectStyle, flex: 1 }}
                      value={exportDpi || 150} onChange={(e) => onExportDpiChange(Number(e.target.value))}>
                      <option value={72}>72 DPI (screen)</option>
                      <option value={150}>150 DPI</option>
                      <option value={300}>300 DPI (print)</option>
                      <option value={600}>600 DPI</option>
                    </select>
                  )}
                </div>
                <button type="button" aria-label="Download layout" title="Download this page"
                  style={{ ...buttonStyle, background: "#ffe600", color: "#1a1a1a", borderColor: "#1a1a1a", fontWeight: 700 }}
                  onClick={() => { onDownload?.(); togglePanel(null); }} disabled={!onDownload}>
                  Download this page
                </button>
                <div style={{ borderTop: "1px solid rgba(0,0,0,0.15)", margin: "2px 0" }} />
                <button type="button" aria-label="Export as JSON" title="Save the layout as a JSON file"
                  style={{ ...buttonStyle, textAlign: "left" }} onClick={() => { onExportJSON?.(); togglePanel(null); }}>
                  Save layout file (JSON)
                </button>
                <button type="button" aria-label="Import from JSON" title="Load a layout JSON file"
                  style={{ ...buttonStyle, textAlign: "left" }} onClick={triggerJSONImport}>
                  Open layout file…
                </button>
                <button type="button" aria-label="Start new layout" title="Clear this page and start fresh"
                  style={{ ...buttonStyle, textAlign: "left" }} onClick={() => { togglePanel(null); onClearSavedData?.(); }}>
                  Start this page over…
                </button>
                <input ref={jsonInputRef} type="file" accept=".json,application/json"
                  style={{ display: "none" }} onChange={handleJSONImport} />
              </div>
            )}
          </div>

          {onToggleDarkMode && (
            <button type="button" title="Toggle dark mode"
              style={darkMode ? activeIconButtonStyle : iconButtonStyle}
              onClick={onToggleDarkMode}>
              <span className="material-icons" aria-hidden="true">{darkMode ? "light_mode" : "dark_mode"}</span>
            </button>
          )}
          <a href="/tutorial" target="_blank" rel="noopener noreferrer" title="Tutorial"
            style={{ ...iconButtonStyle, textDecoration: "none", color: "inherit" }}>
            <span className="material-icons" aria-hidden="true">help_outline</span>
          </a>

        </div>
      )}
    </div>
  );
};

export default React.memo(ToolbarRow1);
